import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import {
  ActiveProviderService,
  API_URL,
  SERVER_API_URL,
  apiServicesProviders,
  AuthenticationService,
  ChatDocumentViewer,
  credentialsInterceptor,
  CARE_TOOLS_CONFIG,
  DIRECTORY_PORTAL_URL,
  GoogleMapsLoaderService,
  GROWTH_CONFIG,
  jwtInterceptor,
  LocalStorageService,
  MARKETING_BASE_URL,
  NAVIGATION_CONFIG,
  provideBreadcrumbOrigins,
  NavigationService,
  PATIENT_QUICK_VIEW_CONTROLLER,
  PatientQuickViewService,
  PORTAL_HEADER_COMPONENT,
  PORTAL_PATIENT_QUICK_VIEW_COMPONENT,
  PORTAL_ROLE,
  PORTAL_SIDEBAR_COMPONENT,
  PORTAL_TELEHEALTH_ACTION_BAR_COMPONENT,
  PORTAL_TELEHEALTH_PANEL_COMPONENT,
  provideAuthBootstrap,
  providePortalTitle,
  provideTelehealthConfig,
  provideGoogleMapsApi,
  ProviderEducationService,
  ProviderPatientsService,
  PROVIDERS_SETUPS_CONFIG,
  ProviderService,
  RefreshTokenStateService,
  SHARED_SERVICES_DEBUG,
  xsrfInterceptor,
} from '@services';

import { DatePipe } from '@angular/common';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ExternalSvgModule, ParseDatePipe } from '@ui-lib';
import { provideQuillConfig } from 'ngx-quill/config';
import { provideGoogleClientId } from '../../../services/src/lib/services/google-client-id.provider';
import { AiLabListBreadcrumbOrigin } from './main/ai-lab/ai-lab-list.component';
import {
  provideMicrosoftClientId,
  provideMicrosoftTenantId,
} from '../../../services/src/lib/services/microsoft-client-id.provider';
import { provideZoomClientId } from '../../../services/src/lib/services/zoom-client-id.provider';
import { TelehealthCallPanelComponent } from '../../../ui-lib/src/lib/telehealth-call-panel/telehealth-call-panel.component';
import { TelehealthActionBarComponent } from './main/shell/telehealth-action-bar/telehealth-action-bar.component';
import { PatientQuickViewComponent } from './main/shell/patient-quick-view/patient-quick-view.component';
import { PatientQuickViewStore } from './main/shell/patient-quick-view/patient-quick-view.store';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { ProviderChatDocumentViewer } from './chat-document-viewer.service';
import { VOICEMAIL_PROVIDERS } from './voicemail.config';
import { ProviderPortalHeaderComponent } from './main/shell/portal-header/provider-portal-header.component';
import { ProviderPortalSidebarMenuComponent } from './main/shell/sidebar-menu/provider-portal-sidebar-menu.component';
import {
  CareToolsConfig,
  GrowthConfig,
  NavigationConfig,
  ProvidersSetupsConfig,
} from './navigation.config';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    providePortalTitle(),
    provideHttpClient(
      withInterceptors([
        credentialsInterceptor,
        jwtInterceptor,
        xsrfInterceptor,
      ])
    ),
    provideAnimationsAsync(),
    importProvidersFrom(ExternalSvgModule),
    provideAuthBootstrap(),
    provideTelehealthConfig(),
    { provide: ChatDocumentViewer, useClass: ProviderChatDocumentViewer },

    ...apiServicesProviders(environment.useMock),
    ...VOICEMAIL_PROVIDERS,

    {
      provide: SHARED_SERVICES_DEBUG,
      useValue: !environment.production,
    },
    {
      provide: PORTAL_HEADER_COMPONENT,
      useValue: ProviderPortalHeaderComponent,
    },
    {
      provide: PORTAL_TELEHEALTH_PANEL_COMPONENT,
      useValue: TelehealthCallPanelComponent,
    },
    {
      provide: PORTAL_TELEHEALTH_ACTION_BAR_COMPONENT,
      useValue: TelehealthActionBarComponent,
    },
    {
      provide: PORTAL_SIDEBAR_COMPONENT,
      useValue: ProviderPortalSidebarMenuComponent,
    },
    {
      provide: PORTAL_PATIENT_QUICK_VIEW_COMPONENT,
      useValue: PatientQuickViewComponent,
    },
    PatientQuickViewService,
    {
      provide: PATIENT_QUICK_VIEW_CONTROLLER,
      useExisting: PatientQuickViewStore,
    },
    { provide: NAVIGATION_CONFIG, useValue: NavigationConfig },
    { provide: PROVIDERS_SETUPS_CONFIG, useValue: ProvidersSetupsConfig },
    { provide: GROWTH_CONFIG, useValue: GrowthConfig },
    { provide: CARE_TOOLS_CONFIG, useValue: CareToolsConfig },
    // An analysis opened from the cross-patient AI Lab list lives under the patient profile, so
    // without this its breadcrumb would only lead back to the patient, never to the list.
    provideBreadcrumbOrigins({
      [AiLabListBreadcrumbOrigin]: {
        label: 'AI Lab',
        url: '/ai-lab',
        // The analysis view carries the triage criteria the practitioner left; rebuild them so the
        // crumb returns to the same filtered, searched list rather than a reset one.
        queryParams: params => ({
          filter: params.get('listFilter'),
          search: params.get('listSearch'),
        }),
      },
    }),
    { provide: API_URL, useValue: environment.apiRootUrl },
    {
      provide: SERVER_API_URL,
      useValue: environment.serverApiRootUrl || environment.apiRootUrl,
    },
    { provide: PORTAL_ROLE, useValue: environment.role },
    { provide: DIRECTORY_PORTAL_URL, useValue: environment.directoryPortalUrl },
    {
      provide: MARKETING_BASE_URL,
      useValue: environment.marketingBaseUrl ?? '',
    },

    AuthenticationService,
    LocalStorageService,
    NavigationService,
    RefreshTokenStateService,
    ProviderService,
    GoogleMapsLoaderService,
    ProviderEducationService,
    ProviderPatientsService,
    ActiveProviderService,
    ParseDatePipe,
    DatePipe,

    provideGoogleMapsApi(environment.googleMapsApiKey || ''),
    provideGoogleClientId(environment.googleOauthClientId || ''),
    provideMicrosoftClientId(environment.microsoftOauthClientId || ''),
    provideMicrosoftTenantId(environment.microsoftOauthTenantId || ''),
    provideZoomClientId(environment.zoomOauthClientId || ''),

    provideQuillConfig({
      modules: {
        syntax: true,
      },
    }),
  ],
};
